import { useState } from "react";
import type { FormEvent } from "react";
import { voices } from "@/mocks/media";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dagkqqrep053ijub2c8g";

const pillars = [
  { icon: "ri-eye-line", title: "看清真相", desc: "关注证据与结果，而非话术与姿态。" },
  { icon: "ri-git-branch-line", title: "厘清机制", desc: "把传播、法律与平台机制讲成链条。" },
  { icon: "ri-compass-3-line", title: "保持边界", desc: "中性与方法论导向，避免针对个体定性。" },
];

const topics = ["辟谣与反转", "律师函与删帖", "水军与控评", "危机复盘", "其他议题"];

type Status = "idle" | "loading" | "success" | "error";

export default function About() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "website_alt") payload.append(key, String(value));
    });

    // 命中蜜罐：模拟普通成功，不发请求
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setError("");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || null;
      const code = parsed?.code as string | undefined;
      const msg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;
      if (!res.ok || code !== "OK" || /spam/i.test(msg)) {
        setStatus("error");
        setError(typeof msg === "string" && msg ? msg : "提交失败，请稍后再试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setError("网络异常，未能提交，请稍后再试。");
    }
  };

  return (
    <section id="about" className="bg-background-100 py-16 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary-500" />
              <span className="text-xs font-medium uppercase tracking-[0.25em] text-primary-500">
                ABOUT US
              </span>
            </div>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              关于声浪观察
            </h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              我们是一档以编辑部方式运作的内容观察站，长期关注娱乐圈舆论与危机公关的交汇地带。当\u201c黑料\u201d与\u201c澄清\u201d频繁反转，我们想提供的不是情绪，而是一套能看懂攻防逻辑的框架。
            </p>
            <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">
              网站定位为行业观察与分析方法论内容：我们会讨论辟谣反转的技术路径、律师函与删帖背后的证据与边界、水军与控评对公共讨论的影响。案例均以中性化方式呈现，不对真实个体作事实性断言。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {pillars.map((p) => (
                <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{p.title}</h3>
                  <p className="mt-1 text-xs leading-6 text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {voices.map((v) => (
              <figure key={v.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-2 text-sm leading-7 text-foreground-800">{v.text}</blockquote>
                <figcaption className="mt-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-user-smile-line text-secondary-700" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">{v.name}</span>
                    <span className="block text-xs text-foreground-500">{v.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <div className="mt-12 overflow-hidden rounded-xl bg-foreground-950 p-6 md:p-10">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-accent-400">NEWSLETTER / PITCH</span>
              <h3 className="mt-3 font-heading text-xl font-bold text-white md:text-2xl">
                订阅精选，或向我们投递一个你关心的选题
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-300">
                留下称呼与邮箱，我们会把值得慢读的观察发送给你；如果你手头有值得拆解的议题，也欢迎一并告诉我们。
              </p>
            </div>

            <form
              id="pitch-form"
              data-readdy-form="选题投递订阅表单"
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  name="称呼"
                  type="text"
                  required
                  placeholder="你的称呼"
                  maxLength={30}
                  className="h-11 rounded-md border border-foreground-700 bg-foreground-900 px-4 text-sm text-background-50 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-500"
                />
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="邮箱地址"
                  maxLength={80}
                  className="h-11 rounded-md border border-foreground-700 bg-foreground-900 px-4 text-sm text-background-50 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-500"
                />
              </div>
              <select
                name="主题方向"
                defaultValue=""
                required
                className="h-11 rounded-md border border-foreground-700 bg-foreground-900 px-4 text-sm text-background-50 outline-none transition-colors focus:border-accent-500"
              >
                <option value="" disabled>
                  你想跟进的主题方向
                </option>
                {topics.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <textarea
                name="留言或选题"
                placeholder="简单描述你的留言或想让我们拆解的议题（500 字以内）"
                rows={3}
                maxLength={500}
                className="resize-none rounded-md border border-foreground-700 bg-foreground-900 px-4 py-3 text-sm text-background-50 outline-none transition-colors placeholder:text-foreground-500 focus:border-accent-500"
              />
              {/* 反垃圾蜜罐字段 */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-hide"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex h-11 items-center justify-center whitespace-nowrap gap-2 rounded-md bg-accent-500 px-6 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" />
                    提交中…
                  </>
                ) : (
                  <>
                    提交订阅 / 选题
                    <i className="ri-send-plane-line text-base" />
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 text-sm text-accent-300">
                  <i className="ri-checkbox-circle-fill" />
                  已收到你的提交，感谢关注，我们会尽快处理。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-start gap-2 text-sm text-primary-300">
                  <i className="ri-error-warning-fill mt-0.5" />
                  {error}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}